import { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle2, Send } from "lucide-react";

export default function CustomBuilds() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    project_type: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.message) {
      toast.error("Email and message are required.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/inquiries", form);
      setDone(true);
      toast.success("Inquiry sent!");
      setForm({ name: "", email: "", project_type: "", message: "" });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
          ? detail.map((d) => d.msg || JSON.stringify(d)).join(" ")
          : "Could not send your message.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="custom-page" className="bg-stone-950 text-stone-50 pt-14 pb-24">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-12 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left */}
          <div className="lg:col-span-5">
            <p className="text-overline text-amber-600 mb-4">Custom Builds</p>
            <h1 className="font-display text-5xl sm:text-6xl font-black tracking-tighter uppercase leading-[0.95]">
              Tell me what
              <br />
              you want built.
            </h1>
            <p className="text-stone-300 mt-8 leading-relaxed">
              Send the details below and the message lands in the shop inbox.
              You'll get a personal reply with a quote, timeline and any
              questions — no bots, no forms-on-forms.
            </p>

            <div className="mt-10 space-y-4 text-stone-400 text-sm">
              <div className="flex gap-4">
                <div className="text-overline text-amber-600 w-32 shrink-0">
                  Lead time
                </div>
                <p>2–6 weeks depending on size and finish.</p>
              </div>
              <div className="flex gap-4">
                <div className="text-overline text-amber-600 w-32 shrink-0">
                  Reach
                </div>
                <p>Local pickup or shipped within the U.S.</p>
              </div>
              <div className="flex gap-4">
                <div className="text-overline text-amber-600 w-32 shrink-0">
                  What I love
                </div>
                <p>
                  Cutting boards, tables, signs, boxes, charcuterie boards,
                  resin inlays.
                </p>
              </div>
            </div>
          </div>

          {/* Right — Form */}
          <div className="lg:col-span-7">
            {done ? (
              <div
                data-testid="inquiry-success"
                className="border border-amber-700/60 bg-stone-900/60 p-10 sm:p-14"
              >
                <CheckCircle2 size={40} className="text-amber-500 mb-6" />
                <h2 className="font-display text-3xl sm:text-4xl font-bold">
                  Got it. Talk soon.
                </h2>
                <p className="text-stone-400 mt-4 leading-relaxed">
                  Your message just hit the shop inbox. You'll get a reply
                  directly to <span className="text-amber-500">your email</span>{" "}
                  — usually within a couple days. In the meantime, take a look
                  at the work that's already built and ready to ship.
                </p>
                <button
                  onClick={() => setDone(false)}
                  data-testid="inquiry-send-another"
                  className="mt-8 text-overline border border-stone-700 hover:border-amber-600 px-6 py-3"
                >
                  Send another
                </button>
              </div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="inquiry-form"
                className="border border-stone-800 bg-stone-900/40 p-8 sm:p-12 space-y-6"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="text-overline text-stone-500 block mb-3">
                      Your name
                    </label>
                    <input
                      type="text"
                      data-testid="inquiry-name"
                      placeholder="optional"
                      value={form.name}
                      onChange={update("name")}
                      className="w-full bg-stone-950 border border-stone-800 focus:border-amber-600 outline-none text-stone-100 placeholder:text-stone-600 p-4 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="text-overline text-stone-500 block mb-3">
                      Email *
                    </label>
                    <input
                      type="email"
                      required
                      data-testid="inquiry-email"
                      placeholder="you@email.com"
                      value={form.email}
                      onChange={update("email")}
                      className="w-full bg-stone-950 border border-stone-800 focus:border-amber-600 outline-none text-stone-100 placeholder:text-stone-600 p-4 transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-overline text-stone-500 block mb-3">
                    Project type
                  </label>
                  <input
                    type="text"
                    data-testid="inquiry-project-type"
                    placeholder="cutting board, table, sign, custom box…"
                    value={form.project_type}
                    onChange={update("project_type")}
                    className="w-full bg-stone-950 border border-stone-800 focus:border-amber-600 outline-none text-stone-100 placeholder:text-stone-600 p-4 transition-colors"
                  />
                </div>

                <div>
                  <label className="text-overline text-stone-500 block mb-3">
                    Tell me about it *
                  </label>
                  <textarea
                    required
                    data-testid="inquiry-message"
                    rows={7}
                    placeholder="dimensions, wood preferences, deadline, any photos or sketches you can describe…"
                    value={form.message}
                    onChange={update("message")}
                    className="w-full bg-stone-950 border border-stone-800 focus:border-amber-600 outline-none text-stone-100 placeholder:text-stone-600 p-4 resize-y transition-colors"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="inquiry-submit"
                  className="bg-amber-700 hover:bg-amber-600 disabled:bg-stone-800 disabled:cursor-not-allowed text-stone-50 px-8 py-5 text-overline w-full sm:w-auto transition-colors flex items-center justify-center gap-3"
                >
                  <Send size={16} /> {submitting ? "Sending…" : "Send inquiry"}
                </button>

                <p className="text-xs text-stone-500 leading-relaxed pt-2">
                  Your message will be emailed to woodbyzq@gmail.com. Replies
                  go directly to your email address.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
